/**
 * EventsTile — «I dag»-kortet på kjøkkenskjermen (Plan 01-14 original; Plan
 * 03-09 upgrade; Stell_Kjokken i #160: tittel «I dag», antall til høyre,
 * store rader med klokkeslett og sted, «+N til» som tekst).
 *
 * D-11 upgrade in place:
 *   - Pulls live data via useTodayEvents() when no `events` prop passed.
 *   - Preserves data-testid="events-title" + data-testid="events-empty" + empty copy.
 *   - Adds data-testid="events-list" with EventCard variant="today-strip".
 *   - All-day events sort to top with 'hele dagen' label.
 *   - Max 4 events; if more, appends data-testid="events-overflow" "+N til" text.
 *   - Tile click routes to /i-dag (D-12).
 *
 * T-CAL-42: 401 from /api/calendar/today → error state → default [] → renders
 * empty-state copy ("Ingen hendelser i dag") — no PII leak in authless idle mode.
 */
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Card } from '../../../components/primitives/Card';
import { EventCard } from '../../calendar/EventCard';
import { useTodayEvents } from '../../today/useTodayEvents';

export type DashboardEvent = {
  id: string;
  title: string;
  /** ISO datetime string. */
  time: string;
  /** NEW — drives the 4-px left colour stripe. */
  color_token: string;
  /** NEW — for AvatarStack in EventCard. */
  attendee_ids: string[];
  /** NEW — floats all-day events to top; shows 'hele dagen' instead of time. */
  all_day: boolean;
  /** Stedet under tittelen på kjøkkenskjermen (#160). */
  location?: string | null;
};

/** Så mange av dagens hendelser vises før «+N» (delt med Hjem, #154). */
export const TODAY_EVENTS_MAX = 4;

/**
 * Heldagshendelser først, deretter etter starttid. Sorter på øyeblikk, ikke
 * streng: serier har Oslo-offset, enkeltavtaler «Z» (#137).
 */
export function sortTodayEvents<T extends { all_day: boolean }>(
  events: readonly T[],
  startOf: (e: T) => string,
): T[] {
  return [...events].sort((a, b) => {
    if (a.all_day && !b.all_day) return -1;
    if (!a.all_day && b.all_day) return 1;
    return Date.parse(startOf(a)) - Date.parse(startOf(b));
  });
}

export function EventsTile({ events }: { events?: DashboardEvent[] }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { data: liveEvents = [] } = useTodayEvents();

  // If explicit `events` prop passed (Phase 1 callers, tests) use that;
  // otherwise map live EventInstance[] → DashboardEvent[].
  const list: DashboardEvent[] =
    events ??
    liveEvents.map((ev) => ({
      id: ev.id,
      title: ev.title,
      time: ev.starts_at,
      color_token: ev.color_token,
      attendee_ids: ev.attendee_ids,
      all_day: ev.all_day,
      location: ev.location,
    }));

  const sorted = sortTodayEvents(list, (e) => e.time);
  const visible = sorted.slice(0, TODAY_EVENTS_MAX);
  const overflow = Math.max(0, sorted.length - TODAY_EVENTS_MAX);

  // Stell_Kjokken (#160): glasskortet «I dag» med antall til høyre og radene
  // under. Hele kortet åpner /i-dag, som før.
  return (
    <Card
      variant="soft"
      onClick={() => nav('/i-dag')}
      aria-label={t('home.today.aria', {
        events:
          sorted.length === 0
            ? t('home.today.none')
            : t('home.today.events', { count: sorted.length }),
      })}
      className="flex h-full min-h-0 cursor-pointer flex-col gap-4.5 overflow-hidden rounded-xl p-6"
    >
      <div className="flex items-baseline justify-between gap-3">
        <p data-testid="events-title" className="m-0 text-heading-l font-extrabold">
          {t('dashboard.idle.today')}
        </p>
        {sorted.length > 0 ? (
          <span className="text-ink-2 text-body font-semibold">
            {t('home.today.events', { count: sorted.length })}
          </span>
        ) : null}
      </div>
      {sorted.length === 0 ? (
        <p data-testid="events-empty" className="text-ink-2 m-0 text-lg">
          {t('dashboard.events.empty')}
        </p>
      ) : (
        <ul data-testid="events-list" className="m-0 flex list-none flex-col gap-4.5 p-0">
          {visible.map((e) => (
            <li key={e.id}>
              <EventCard
                variant="today-strip"
                id={e.id}
                title={e.title}
                starts_at={e.time}
                ends_at={null}
                all_day={e.all_day}
                location={e.location ?? null}
                color_token={e.color_token}
                attendee_ids={e.attendee_ids}
              />
            </li>
          ))}
          {overflow > 0 && (
            // Tekst, ikke knapp: kortet rundt åpner allerede /i-dag.
            <li data-testid="events-overflow" className="text-ink-2 text-body font-bold">
              {t('calendar.eventsTile.overflow', { count: overflow })}
            </li>
          )}
        </ul>
      )}
    </Card>
  );
}
